import type { Address } from '../../api/agwa-api-model';

/**
 * Address rendering, shared by every surface that shows one.
 *
 * This mirrors `Address.ToDisplayString` on the server deliberately: the same site must read
 * identically in the editor, in the read-only panel and on a certificate. These two UI components
 * previously composed addresses independently and had already drifted - one wrote "Unit 5, 25
 * Smith St" while the other wrote "5/25 Smith St" - which is why the logic lives here now.
 *
 * See documentation/engineering/site-address-google-first.md.
 */

const val = (
  address: Partial<Address> | null | undefined,
  key: keyof Address
): string => {
  const v: unknown = address?.[key];
  return typeof v === 'string' ? v.trim() : '';
};

/**
 * A bare unit number such as "5", "12" or "5A" - the form the Australian slash convention is
 * written for. Anything descriptive ("Unit 5, Lvl 4") has to be comma separated instead, or it
 * renders as "unit 5, lvl 4/25 Harbour Street".
 */
const isBareUnitNumber = (unit: string): boolean =>
  unit.length > 0 && unit.length <= 5 && /^[0-9][a-z0-9]*$/i.test(unit);

/** The street line: "5/25 Harbour Street", or "Unit 5, Lvl 4, 25 Harbour Street". */
export function composeStreet(
  address: Partial<Address> | null | undefined
): string {
  const street = [val(address, 'streetNumber'), val(address, 'streetName')]
    .filter(Boolean)
    .join(' ');
  const unit = val(address, 'unitNumber');

  if (!unit) return street;
  if (!street) return unit;

  return isBareUnitNumber(unit) ? `${unit}/${street}` : `${unit}, ${street}`;
}

/** Suburb, state and postcode as one line. */
export function composeLocality(
  address: Partial<Address> | null | undefined
): string {
  return [
    val(address, 'suburb'),
    val(address, 'state'),
    val(address, 'postcode'),
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * True when the components can form a complete address on their own. Below that Google's formatted
 * address wins, because it is complete by construction - composing a street with no locality would
 * render "123 Main Street" and drop the rest.
 */
export function hasComposableAddress(
  address: Partial<Address> | null | undefined
): boolean {
  return Boolean(composeStreet(address)) && Boolean(composeLocality(address));
}

/** Words, lowercased and stripped of punctuation. */
const tokenise = (value: string): string[] =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean);

/**
 * True when `candidate` reads as a shortening of `word` - a prefix no more than half its length,
 * such as "st" for "street". Near-equal lengths ("australia" for "australian") are different words.
 */
const isAbbreviationOf = (candidate: string, word: string): boolean =>
  candidate.length >= 2 &&
  candidate.length * 2 <= word.length &&
  word.startsWith(candidate);

/**
 * True when the place name only repeats the address line, so showing both reads as a stutter -
 * "25 Harbour St / 25 Harbour Street".
 *
 * Redundant only when the name is a word-prefix of the line - the "25 Harbour St" case, where the
 * name just repeats the start of the street. A name with more words than the line is a business
 * name and is always kept. Only the final name word may be an abbreviation, and only a decisive
 * one, so "St" collapses into "Street" but "Australian" is not swallowed by "Australia".
 *
 * Mirrors `Address.IsRedundantName` on the server - the two must agree, or a site reads differently
 * in the editor than it does on a certificate.
 */
export function isRedundantPlaceName(
  placeName: string,
  addressLine: string
): boolean {
  const name = tokenise(placeName);
  const line = tokenise(addressLine);

  // Redundant only when the name is the start of the line and nothing more - "25 Harbour St"
  // repeating the street. A name with more words than the line (a business name) carries
  // information the line does not. Comparing shorter-against-longer let short state codes pose as
  // abbreviations of longer name words ("SA" of "Safety"), so the direction is fixed here.
  if (name.length === 0 || name.length > line.length) return false;

  return name.every((word, i) => {
    if (word === line[i]) return true;
    // Only the final name word may differ, and only as a decisive abbreviation ("St" for "Street").
    return i === name.length - 1 && isAbbreviationOf(word, line[i]);
  });
}

/**
 * The readable address as display lines. The place name leads because it is a locating component -
 * for a business or landmark it is the only part that says whose site this is.
 */
export function addressLines(
  address: Partial<Address> | null | undefined
): string[] {
  const lines: string[] = [];
  const placeName = val(address, 'placeName');
  const street = composeStreet(address);

  const body: string[] = [];
  if (!hasComposableAddress(address) && val(address, 'formattedAddress')) {
    body.push(val(address, 'formattedAddress'));
  } else {
    if (street) body.push(street);
    const tail = [composeLocality(address), val(address, 'country')]
      .filter(Boolean)
      .join(', ');
    if (tail) body.push(tail);
  }

  if (placeName && !isRedundantPlaceName(placeName, body[0] ?? '')) {
    lines.push(placeName);
  }

  return [...lines, ...body];
}

/** The whole address on one line, for seeding the lookup box. */
export function singleLine(
  address: Partial<Address> | null | undefined
): string {
  return addressLines(address).join(', ');
}
